import type { RegisterStatusCallBackStatusType } from '@/components/RegisterPage/factory-helpers';

// Descriptive snake_case vocabulary for the `error` property of
// `Registration Failed`. The human-readable text goes in `error_message`, and
// the numeric HTTP status (when the failure is an HTTP one) goes in
// `error_code`. Keep in sync with the mobile counterpart at
// `pierre-mobile/src/features/analytics/utils/registrationErrorCode.ts`.
//
// Names say WHAT failed — in a "Registration Failed" event, a bucket like
// `failed`/`registration_failed` is tautological and un-aggregatable, so we
// avoid it in favour of specific causes plus a clearly-labelled catch-all.
export const REGISTRATION_ERROR_CODE = {
  invalid_data: 'invalid_data',
  email_domain_typo: 'email_domain_typo',
  email_duplicated: 'email_duplicated',
  phone_duplicated: 'phone_duplicated',
  document_duplicated: 'document_duplicated',
  signup_backend_error: 'signup_backend_error',
  cpf_validation_failed: 'cpf_validation_failed',
  rate_limited: 'rate_limited',
  timeout: 'timeout',
  network_error: 'network_error',
  server_error: 'server_error',
  unexpected_error: 'unexpected_error',
} as const;

export type RegistrationErrorCode =
  (typeof REGISTRATION_ERROR_CODE)[keyof typeof REGISTRATION_ERROR_CODE];

/**
 * Email fields for a `Registration Failed` payload, minimised per error code.
 *
 * For a refused email domain the only actionable signal is the **suffix**:
 * `CONFIRMED_INVALID_SUFFIXES` is keyed on it, so `comm` is what tells us a new
 * typo suffix is appearing and the blocklist needs extending.
 *
 * Nothing before the suffix is reported. The local part identifies a person and
 * the second-level label identifies an employer — `secretcorp.comm` would
 * disclose where somebody works, and RudderStack enriches events with an
 * AppsFlyer identifier, which makes that linkable telemetry rather than an
 * anonymous counter. The full domain bought no extra signal for the blocklist
 * decision, so it is dropped (LGPD data minimisation; the repo rule is to mask
 * personal data in analytics).
 *
 * Every other code keeps the existing `email` field — this deliberately does
 * not change the established payload for failures the team already relies on.
 */
export function registrationFailureEmailFields(
  code: string,
  email: string | undefined,
): { email?: string; email_suffix?: string } {
  if (!email) return {};
  if (code !== REGISTRATION_ERROR_CODE.email_domain_typo) return { email };

  const at = email.lastIndexOf('@');
  if (at === -1) return {};

  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  const suffix = domain.split('.').pop();
  return suffix ? { email_suffix: suffix } : {};
}

// Maps the public register server-action status to a descriptive `error` code.
export function registrationErrorCodeFromStatus(
  status: RegisterStatusCallBackStatusType,
): RegistrationErrorCode {
  switch (status) {
    case 'invalid_data':
      return REGISTRATION_ERROR_CODE.invalid_data;
    case 'invalid_email_domain':
      return REGISTRATION_ERROR_CODE.email_domain_typo;
    case 'cloudwalk_unavailable':
      return REGISTRATION_ERROR_CODE.signup_backend_error;
    case 'cloudwalk_validation_error':
      return REGISTRATION_ERROR_CODE.cpf_validation_failed;
    default:
      // `failed` and any unmapped status degrade to the catch-all.
      return REGISTRATION_ERROR_CODE.unexpected_error;
  }
}
